import { SectionLabel, FadeUp } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const PackageCard = ({ pkg, index, t }) => (
  <FadeUp delay={index * 0.12} className="h-full">
    <div
      data-testid={`package-card-${["bartender-service","essential-bar-service","hudson-stir-experience"][index]}`}
      className={`flex h-full flex-col bg-midnight p-10 transition-colors duration-500 lg:p-12 ${
        index === 2 ? "border border-gold/70" : "border border-gold/20 hover:border-gold/45"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <span className="font-sans text-[10px] tracking-[0.4em] text-gold">
          {t.packages.serviceWord} {pkg.n}
        </span>
        {pkg.badge && (
          <span className="font-sans text-[9px] tracking-[0.25em] text-gold/70">
            {pkg.badge}
          </span>
        )}
      </div>
      <h3 className="mt-6 font-serif text-2xl font-light leading-snug tracking-wide text-ivory lg:text-3xl">
        {pkg.name.toUpperCase()}
      </h3>
      <p className="mt-3 font-serif text-lg italic leading-snug text-ivory/55">{pkg.tagline}</p>
      <p className="mt-6 font-serif text-3xl font-light text-gold">{pkg.price}</p>
      <p className="mt-4 font-sans text-[11px] font-light tracking-[0.18em] text-gold/80">
        {pkg.capacity}
      </p>

      <div className="my-8 h-px bg-gold/20" />

      <ul className="flex flex-col gap-3.5">
        {pkg.features.map((line) => (
          <li key={line} className="flex items-center gap-3">
            <span className="h-1 w-1 rotate-45 bg-gold" aria-hidden="true" />
            <span className="font-sans text-sm font-light text-ivory/70">{line}</span>
          </li>
        ))}
      </ul>

      <p className="mt-7 font-sans text-xs font-light italic text-ivory/45">
        {pkg.client}
      </p>
    </div>
  </FadeUp>
);

const Packages = () => {
  const { t } = useLang();
  return (
  <section id="packages" data-testid="packages-section" className="bg-charcoal">
    <div className="mx-auto max-w-[1680px] px-6 py-20 lg:px-12 lg:py-32">
      <SectionLabel index="04" text={t.packages.label} />
      <FadeUp delay={0.1}>
        <h2 className="mt-8 max-w-2xl font-serif text-4xl font-light leading-[1.1] text-ivory md:text-5xl lg:text-6xl">
          {t.packages.pre}<span className="italic text-gold">{t.packages.em}</span>
        </h2>
      </FadeUp>

      <div className="mt-16 grid gap-8 lg:grid-cols-3">
        {t.packages.items.map((pkg, i) => (
          <PackageCard key={pkg.n} pkg={pkg} index={i} t={t} />
        ))}
      </div>

      <FadeUp delay={0.15} className="mt-16">
        <div
          data-testid="packages-custom-events"
          className="border border-gold/20 bg-midnight p-10 text-center lg:p-14"
        >
          <p className="font-sans text-[10px] tracking-[0.4em] text-gold">
            {t.packages.custom.label}
          </p>
          <h3 className="mt-5 font-serif text-3xl font-light text-ivory md:text-4xl">
            {t.packages.custom.pre}<span className="italic text-gold">{t.packages.custom.em}</span>
          </h3>
          <p className="mx-auto mt-5 max-w-2xl font-sans text-sm font-light leading-relaxed text-ivory/55">
            {t.packages.custom.body}
          </p>
        </div>
      </FadeUp>

      <FadeUp delay={0.2}>
        <p
          data-testid="packages-dry-hire-note"
          className="mx-auto mt-12 max-w-2xl text-center font-sans text-sm font-light leading-relaxed text-ivory/45"
        >
          {t.packages.dryHire}
        </p>
        <p className="mx-auto mt-6 max-w-3xl text-center font-sans text-xs font-light leading-relaxed text-ivory/35">
          {t.packages.disclaimer}
        </p>
      </FadeUp>
    </div>
  </section>
  );
};

export default Packages;
